import { Component, OnInit, OnDestroy, ElementRef, Directive, HostListener, Inject, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { DirectionService } from '../../direction.service';
import { AuthService } from 'src/app/auth/auth.service';
import { DialogService } from 'src/app/dialog/dialog.service';
import { UsersService } from 'src/app/super-management/user/users.service';
import { OtpCodeInputComponent } from 'src/app/auth/otp-code-input/otp-code-input.component';
import { formatCountdown, otpErrorKey } from 'src/app/auth/otp-errors';
import { formatIsraeliMobile, formatStoredPhone, isValidIsraeliMobile } from 'src/app/utils/israeli-mobile';

@Directive({
  selector: '[appPhoneFormatDialog]'
})

export class PhoneFormatDialogDirective {
  constructor(private el: ElementRef) { }

  @HostListener('input', ['$event']) onInputChange(event) {
    const initialValue = this.el.nativeElement.value;

    this.el.nativeElement.value = initialValue.replace(/[^0-9]/g, '')
      .replace(/(\d{2})(\d{4})(\d{3})/, '0$1-$2-$3');

    if (initialValue !== this.el.nativeElement.value) {
      event.stopPropagation();
    }
  }
}

/**
 * Add / change the account phone, verified by a 6-digit SMS code. The number must not belong to another account.
 * mandatory: opened after login for an account without a phone — no close button; verify a phone or log out.
 * sharedPhone: the account's unverified number is also on another account — confirm it (or enter another).
 */
@Component({
  selector: 'app-phone',
  templateUrl: './phone.component.html',
  styleUrls: ['./phone.component.css'],
  host: {
    class: 'fill-screen-modal-phone'
  }
})
export class PhoneComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  private authStatusSub: Subscription;
  isDarkMode: boolean = false;
  isLoading: boolean = false;

  mandatory = false;
  isSharedPhone = false;
  step: 'phone' | 'code' | 'done' = 'phone';
  phone = '';
  savedPhone: number = null;
  errorKey = '';
  codeAttemptsLeft: number | null = null;
  resendCountdown = 0;
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild(OtpCodeInputComponent) otpCodeInput?: OtpCodeInputComponent;

  readonly formatStoredPhone = formatStoredPhone;

  constructor(
    private directionService: DirectionService,
    private authService: AuthService,
    private dialogService: DialogService,
    private usersService: UsersService,
    @Optional() @Inject(MAT_DIALOG_DATA) data: { mandatory?: boolean; sharedPhone?: number | null } | null,
  ) {
    this.mandatory = !!data?.mandatory;
    if (data?.sharedPhone) {
      this.isSharedPhone = true;
      this.phone = formatStoredPhone(data.sharedPhone);
    }
  }

  get titleKey(): string {
    if (this.isSharedPhone) {
      return 'phone-dialog-shared-title';
    }
    return this.mandatory ? 'phone-dialog-mandatory-title' : 'phone-dialog-title';
  }

  get explainKey(): string {
    if (this.isSharedPhone) {
      return 'phone-dialog-shared-explain';
    }
    return this.mandatory ? 'phone-dialog-mandatory-explain' : 'phone-dialog-verify-explain';
  }

  ngOnInit() {
    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });

    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe(isAuthenticated => {
        // logged out (button below, or the session expired) — nothing to verify anymore
        if (!isAuthenticated) {
          this.closePhoneDialog();
        }
      });
  }

  ngOnDestroy() {
    this.stopResendCountdown();
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
    }
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
    if (this.authStatusSub) {
      this.authStatusSub.unsubscribe();
    }
  }

  get isPhoneValid(): boolean {
    return isValidIsraeliMobile(this.phone);
  }

  get formattedPhone(): string {
    return formatIsraeliMobile(this.phone);
  }

  get resendCountdownLabel(): string {
    return formatCountdown(this.resendCountdown);
  }

  closePhoneDialog() {
    this.dialogService.onClosePhoneDialog();
  }

  onPhoneInput(input: HTMLInputElement) {
    this.phone = formatIsraeliMobile(input.value);
    input.value = this.phone;
    this.errorKey = '';
  }

  onSendCode() {
    if (!this.isPhoneValid || this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.errorKey = '';
    this.authService.sendPhoneVerificationCode(this.phone).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (!response) {
          this.errorKey = otpErrorKey(null);
          return;
        }
        this.codeAttemptsLeft = null;
        this.step = 'code';
        this.startResendCountdown(response.resendIn);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorKey = otpErrorKey(error);
        if (error?.error?.message === 'OTP_RESEND_TOO_SOON') {
          // a code was already sent to this number a moment ago
          this.step = 'code';
          this.errorKey = '';
          this.startResendCountdown(error.error.retryAfter);
        }
      }
    });
  }

  onVerifyCode(code: string) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.errorKey = '';
    this.authService.verifyPhoneCode(this.phone, code).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (!response) {
          this.errorKey = otpErrorKey(null);
          this.otpCodeInput?.clear();
          return;
        }
        this.savedPhone = response.phone;
        this.usersService.updatePhone(response.phone);
        this.step = 'done';
        this.stopResendCountdown();
        this.closeTimer = setTimeout(() => this.closePhoneDialog(), 1200);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorKey = otpErrorKey(error);
        this.codeAttemptsLeft = error?.error?.attemptsLeft ?? null;
        if (error?.error?.message === 'PHONE_ALREADY_IN_USE') {
          this.step = 'phone';
          return;
        }
        this.otpCodeInput?.clear();
      }
    });
  }

  get isCodeExpired(): boolean {
    return this.errorKey === 'otp-error.OTP_EXPIRED';
  }

  /** After too many wrong attempts the code is dead server-side; only a new code helps. */
  get isCodeLocked(): boolean {
    return this.errorKey === 'otp-error.OTP_TOO_MANY_ATTEMPTS';
  }

  onResendCode() {
    if (this.resendCountdown > 0) {
      return;
    }
    this.otpCodeInput?.clear();
    this.onSendCode();
  }

  onChangePhone() {
    this.step = 'phone';
    this.errorKey = '';
    this.stopResendCountdown();
  }

  onLogout() {
    this.authService.logout();
  }

  private startResendCountdown(seconds: number) {
    this.stopResendCountdown();
    this.resendCountdown = seconds || 60;
    this.resendTimer = setInterval(() => {
      this.resendCountdown = Math.max(0, this.resendCountdown - 1);
      if (this.resendCountdown === 0) {
        this.stopResendCountdown();
      }
    }, 1000);
  }

  private stopResendCountdown() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }
}
